import React, { useState, useRef, useEffect } from 'react';
import { Guess } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { getAvatarHue, ordinal } from '../utils/avatar';
import { hasFinePointer, isTextEditableElement } from '../utils/dom';

interface GuessChatProps {
  guesses: Guess[];
  onGuessSubmit: (guess: string) => void;
  disabled: boolean;
  currentPlayerId: string;
  currentPlayerName: string;
  closeGuesses?: Guess[];
  themeVotePassed: boolean;
}

const GuessChat: React.FC<GuessChatProps> = ({ 
  guesses, 
  onGuessSubmit, 
  disabled, 
  currentPlayerId,
  currentPlayerName,
  closeGuesses = [],
  themeVotePassed
}) => {
  const [guess, setGuess] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Players often start typing before the guess box has focus. Instead of letting those keys hit
   * the page (where Backspace historically meant "go back"), route them into the input, the way
   * skribbl-style games do. Only when nothing else editable is focused.
   */
  useEffect(() => {
    if (disabled) return;

    const focusOnTyping = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTextEditableElement(event.target)) return;

      const isPrintable = event.key.length === 1;
      if (!isPrintable && event.key !== 'Backspace' && event.key !== 'Enter') return;

      const input = inputRef.current;
      if (!input || input.disabled) return;

      input.focus();
      // Nothing to delete yet; make sure the browser never treats this as navigation.
      if (event.key === 'Backspace') {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', focusOnTyping);
    return () => window.removeEventListener('keydown', focusOnTyping);
  }, [disabled]);

  // Put the cursor in the guess box when a round opens (desktop only: avoid popping mobile keyboards).
  useEffect(() => {
    if (!disabled && hasFinePointer()) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  // Get only correct guesses for displaying in the correct answers section
  const correctGuesses = guesses.filter(g => g.isCorrect);
  // Get reward messages
  const rewardMessages = guesses.filter(g => g.isReward);
  // Everything else (regular guesses, theme votes, system notes) renders in the stream
  const streamMessages = guesses.filter(g => !g.isCorrect && !g.isReward);
  
  const songSelectorTotalRewards = themeVotePassed ? 0 : rewardMessages.reduce((acc) => acc + 50, 0);
  const songSelectorName = currentPlayerName;

  useEffect(() => {
    // Scroll to bottom when new guesses come in
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [streamMessages.length, closeGuesses.length]);

  useEffect(() => {
    // Drop any unsent draft when the round closes so it doesn't leak into the next one
    if (disabled) {
      setGuess('');
    }
  }, [disabled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!guess.trim() || disabled) {
      return;
    }
    
    onGuessSubmit(guess);
    setGuess('');
  };

  const isEmpty = streamMessages.length === 0 && closeGuesses.length === 0;
  const emptyStateText = correctGuesses.length > 0
    ? 'No other guesses this round.'
    : disabled
      ? 'Guesses will show up here.'
      : 'No guesses yet. Be the first!';

  return (
    <div className="guess-chat">
      <div className="panel-heading">
        <h3>Guesses</h3>
        {correctGuesses.length > 0 && (
          <span className="count-pill count-pill--success">{correctGuesses.length} solved</span>
        )}
      </div>

      {/* Correct guesses notification area */}
      <AnimatePresence initial={false}>
        {correctGuesses.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="correct-panel"
          >
            <ul className="correct-list">
              {correctGuesses.map((guessItem, index) => {
                const isCurrentPlayersGuess = guessItem.playerId === currentPlayerId;
                
                return (
                  <motion.li 
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    key={`correct-${guessItem.playerId}-${index}`} 
                    className="correct-row"
                  >
                    <span className="correct-row-main">
                      <span className="correct-check" aria-hidden="true">✓</span>
                      <strong>{guessItem.username}</strong>
                      {isCurrentPlayersGuess && (
                        <em className="correct-answer">“{guessItem.guess}”</em>
                      )}
                    </span>
                    {guessItem.score !== undefined && (
                      <span className="correct-pts">
                        +{guessItem.score}
                        {guessItem.position && <small>{ordinal(guessItem.position)}</small>}
                      </span>
                    )}
                  </motion.li>
                );
              })}
              <li className="correct-row correct-row--picker">
                <span className="correct-row-main">
                  <span className="correct-check correct-check--picker" aria-hidden="true">♪</span>
                  <strong>{songSelectorName}</strong>
                  <em className="correct-answer">song picker</em>
                </span>
                <span className="correct-pts">+{songSelectorTotalRewards}</span>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="chat-messages" ref={chatContainerRef}>
        {isEmpty ? (
          <div className="chat-empty-state">
            <span className="chat-empty-icon" aria-hidden="true">💬</span>
            <p>{emptyStateText}</p>
          </div>
        ) : (
          <div className="messages-list">
            <AnimatePresence initial={false}>
              {streamMessages.map((message, index) => {
                if (message.isSystemMessage) {
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      key={`sys-${index}`}
                      className="msg msg--system"
                    >
                      {message.guess}
                    </motion.div>
                  );
                }

                const isMine = message.playerId === currentPlayerId;
                return (
                  <motion.div 
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={`${message.playerId}-${index}`} 
                    className={`msg${message.isVote ? ' msg--vote' : ''}${isMine ? ' msg--mine' : ''}`}
                  >
                    <span
                      className="msg-sender"
                      style={{ ['--hue' as string]: getAvatarHue(message.username) }}
                    >
                      {message.username}
                    </span>
                    <span className="msg-text">
                      {message.isVote 
                        ? `voted that this song doesn't fit the theme`
                        : message.guess}
                    </span>
                  </motion.div>
                );
              })}
              
              {/* Display close guesses (only visible to the user who made them) */}
              {closeGuesses.map((closeGuess, index) => (
                <motion.div 
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  key={`close-${closeGuess.playerId}-${index}`} 
                  className="msg msg--close"
                >
                  <span className="msg-sender">You</span>
                  <span className="msg-text">{closeGuess.guess}</span>
                  <span className="msg-note">So close! Check your spelling.</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="chat-form">
        <input
          ref={inputRef}
          type="text"
          value={guess}
          onChange={(e) => setGuess(e.target.value)}
          placeholder={disabled ? 'Waiting for next round…' : 'Type your guess…'}
          disabled={disabled}
          className="input chat-input"
          aria-label="Your guess"
          autoComplete="off"
        />
        <button 
          type="submit" 
          disabled={disabled || !guess.trim()} 
          className="btn btn-primary btn-icon chat-send"
          aria-label="Send guess"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 12l16-8-6 16-2.5-6.5L4 12z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default GuessChat;
